import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, callFunction } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import type { CouponRow } from "@/types/database";
import type { CouponBet } from "@/types/domain";
import type { CouponType } from "@/lib/constants";

export function useUserCoupons() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["coupons", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<CouponRow[]> => {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCoupon(id: string | undefined) {
  return useQuery({
    queryKey: ["coupon", id],
    enabled: !!id,
    refetchInterval: (q) => {
      const c = q.state.data as CouponRow | undefined;
      return c?.status === "pending" ? 60_000 : false;
    },
    queryFn: async (): Promise<CouponRow | null> => {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useSubmitCoupon() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { type: CouponType; bets: CouponBet[] }) => {
      return callFunction<{ couponId: string }>("coupon-submit", {
        type: input.type,
        bets: input.bets.map((b) => ({ matchId: b.matchId, bet: b.bet })),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons", user?.id] }),
  });
}
